/**
 * analytics.ts — single-source tracking facade (ported from the legacy CRA app).
 *
 * Every call site uses only:
 *   import { init, identify, track, pageview, reset, trackCharge, EVENTS } from '@/lib/analytics';
 *
 * Adding a new tracking vendor (Segment, Amplitude, PostHog, FB Pixel, ...):
 *   1. Implement an adapter object with the methods listed in `VENDORS` below.
 *   2. Push it to the `VENDORS` array.
 *   3. Done — no other files need to change.
 *
 * Adding a new event:
 *   1. Add a key to the `EVENTS` constant (canonical naming, "Object Verb" past tense).
 *   2. Call `track(EVENTS.YOUR_NEW_ONE, { props })` from the call site.
 *
 * Behavior is preserved 1:1 from the source JS module with three changes only:
 *   (a) the Mixpanel token now comes from `import.meta.env.VITE_MIXPANEL_TOKEN`,
 *   (b) JS → TS typing, and
 *   (c) when the token is empty the whole facade NO-OPs (never initializes,
 *       never throws — every public method is a safe no-op).
 */

import type { Mixpanel, Config, Dict } from 'mixpanel-browser';
import { EVENTS, type EventKey, type EventName } from './events';

export { EVENTS };
export type { EventKey, EventName };

// ============================================================================
// Configuration
// ============================================================================

const IS_PROD = import.meta.env.PROD;
const APP_VERSION = '1.6.6';
const APP_NAME = 'reactjs';

// Token — read from the Vite env. May be EMPTY in dev: when empty, the facade
// no-ops (the Mixpanel adapter never initializes).
const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN || '';
// EU-region project (data residency in the EU).
const MIXPANEL_API_HOST = 'https://api-eu.mixpanel.com';
const MIXPANEL_REGION = 'eu';

// `record_heatmap_data` is a real Mixpanel option but is not present in the
// shipped `Config` typings, so extend `Partial<Config>` to allow it.
type MixpanelInitOptions = Partial<Config> & { record_heatmap_data?: boolean };

// Mixpanel init options — based on user-provided config with recommended additions.
const MIXPANEL_OPTS: MixpanelInitOptions = {
  debug: !IS_PROD,
  // Disable Mixpanel auto-pageview ("[Auto] Page View") — we emit our own
  // path-suffixed event below so the event name itself is the page (e.g.
  // "Page Viewed /dashboard"), letting Mixpanel charts break down by URL
  // straight from the event picker.
  track_pageview: false,
  persistence: 'localStorage',
  autocapture: false,
  api_host: MIXPANEL_API_HOST,
  // Session replay: sample 30% in prod only — dev sessions waste quota.
  record_sessions_percent: IS_PROD ? 30 : 0,
  record_heatmap_data: true,
  batch_requests: true,
  // Ignore Do-Not-Track. Firefox/Brave in private windows set DNT by default,
  // which would silently disable tracking for those sessions otherwise.
  // Product analytics convention is to ignore DNT — switch to `false` here
  // if you need to honor it (e.g. for an EU-only privacy stance).
  ignore_dnt: true,
  // Session replay PII gate: any DOM element with class="mp-no-record" is fully blocked
  // (avatars in private chat, payment forms). Class="mp-mask" only masks text.
  record_block_class: 'mp-no-record',
  record_mask_text_class: 'mp-mask',
  // Drain queued events as soon as the SDK is ready.
  loaded: () => _onAdapterReady(),
};

// ============================================================================
// Public types
// ============================================================================

/** Free-form analytics property bag. */
export type Props = Record<string, unknown>;

/** Shape of an API response as consumed by `withResult`. */
export interface ApiResult {
  success?: boolean;
  message?: string;
  code?: string | number;
  data?: { message?: string } | null;
  [key: string]: unknown;
}

/** Normalized success/failure props produced by `withResult`. */
export interface ResultProps {
  success: boolean;
  error_message?: string;
  error_code?: string | number;
}

/** The full user object (from Redux) passed to `identify`. */
export interface AnalyticsUser {
  ID?: string | number;
  username?: string;
  firstname?: string;
  lastname?: string;
  image?: string;
  thumbnail?: string;
  plan?: string;
  gender?: string;
  birthdate?: string;
  city?: string;
  state?: string;
  country?: string;
  datetime_created?: string;
  emailverify?: unknown;
  profile?: {
    age?: string | number;
    city?: string;
    state?: string;
    country?: string;
    location_text?: string;
    location?: unknown;
  };
  extra?: {
    age?: string | number;
    location?: string;
  };
  complete_profile?: { per?: number };
  // `unknown` so any concrete `User.subscription` interface — including
  // ones without an index signature (like `UserSubscription` in
  // `@/types/api`) — assigns cleanly. The adapter narrows with a
  // runtime `typeof` check before reading `.plan`. Historical
  // `UserSubscription` doesn't even have a top-level `plan`, so the
  // narrow returns undefined at runtime — intentional best-effort
  // behavior preserved during the legacy port.
  subscription?: unknown;
  // No index signature: an `[key: string]: unknown` here would force
  // every caller to pass a Record-shaped object, breaking assignments
  // from concrete typed interfaces like `User` (which carry their own
  // exhaustive field list). Adapters that need to read fields outside
  // this list should cast the value at the call site.
}

/** People-profile traits synthesized by `_userTraits`. */
interface UserTraits {
  $name?: string;
  $email?: string;
  $first_name?: string;
  $last_name?: string;
  $avatar?: string;
  $created?: string;
  username?: string;
  plan: string;
  gender?: string;
  birthdate?: string;
  age?: number;
  age_bucket?: string;
  city?: string;
  state?: string;
  country?: string;
  location?: string;
  photo?: string;
  profile_completion?: number;
  is_email_verified: boolean;
  has_subscription: boolean;
}

// ============================================================================
// Internal state
// ============================================================================

const _superProps: Props = {}; // merged into every track call
type QueueEntry = [method: string, args: unknown[]];
const _queue: QueueEntry[] = []; // events fired before adapters finish loading
const _state = { ready: false }; // facade-level ready flag
let _pendingIdentity: AnalyticsUser | null = null; // user if identify() called before init

// ============================================================================
// First-touch / last-touch attribution (UTM + referrer)
// ============================================================================

function _captureAttribution(): void {
  try {
    const url = new URL(window.location.href);
    const keys = [
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_term',
      'utm_content',
      'gclid',
      'fbclid',
      'ref',
    ];
    const utm: Record<string, string> = {};
    keys.forEach((k) => {
      const v = url.searchParams.get(k);
      if (v) utm[k] = v;
    });

    if (Object.keys(utm).length) {
      // Last touch — always overwrite (session-scoped).
      Object.entries(utm).forEach(([k, v]) => sessionStorage.setItem('lt_' + k, v));
      // First touch — write once, never overwrite (lifetime).
      if (!localStorage.getItem('ft_captured')) {
        Object.entries(utm).forEach(([k, v]) => localStorage.setItem('ft_' + k, v));
        localStorage.setItem('ft_captured', '1');
        localStorage.setItem('ft_landing_url', url.pathname);
        localStorage.setItem('ft_referrer', document.referrer || '');
      }
    }

    const first: Record<string, string | null> = {};
    const last: Record<string, string | null> = {};
    Object.keys(localStorage)
      .filter((k) => k.startsWith('ft_') && k !== 'ft_captured')
      .forEach((k) => (first[k.slice(3)] = localStorage.getItem(k)));
    Object.keys(sessionStorage)
      .filter((k) => k.startsWith('lt_'))
      .forEach((k) => (last[k.slice(3)] = sessionStorage.getItem(k)));

    if (Object.keys(first).length) _superProps.first_touch = first;
    if (Object.keys(last).length) _superProps.last_touch = last;
    _superProps.http_referrer = document.referrer || null;
    _superProps.referrer_domain = document.referrer ? new URL(document.referrer).hostname : null;
  } catch {
    /* ignore */
  }
}

// ============================================================================
// Vendor adapters
// Each adapter implements (any subset of): init, identify, track, pageview,
// reset, trackCharge, registerSuper. Missing methods are silently skipped.
// ============================================================================

interface VendorAdapter {
  name: string;
  init?: () => void | Promise<void>;
  identify?: (id: string | number, traits?: Props) => void;
  track?: (event: string, props?: Props) => void;
  pageview?: (path?: string, title?: string) => void;
  reset?: () => void;
  trackCharge?: (amount: number, props?: Props) => void;
  registerSuper?: (props: Props) => void;
}

const MixpanelAdapter = {
  name: 'mixpanel',
  _mp: null as Mixpanel | null,

  async init(): Promise<void> {
    if (!MIXPANEL_TOKEN) {
      if (!IS_PROD) console.warn('[analytics] Mixpanel token missing — skipping init.');
      return;
    }
    // Lazy-load (~22KB gzipped). Queue handles events fired before this resolves.
    const mod = await import('mixpanel-browser');
    this._mp = mod.default as unknown as Mixpanel;
    this._mp.init(MIXPANEL_TOKEN, MIXPANEL_OPTS as Partial<Config>);
    this._mp.register({ app: APP_NAME, app_version: APP_VERSION });
    if (!IS_PROD) {
      const masked =
        MIXPANEL_TOKEN.length > 8
          ? MIXPANEL_TOKEN.slice(0, 4) + '…' + MIXPANEL_TOKEN.slice(-4)
          : MIXPANEL_TOKEN;
      console.log('[analytics] Mixpanel initialized', {
        token: masked,
        api_host: MIXPANEL_API_HOST,
        region: MIXPANEL_REGION,
      });
    }
  },

  identify(id: string | number, traits?: Props): void {
    if (!this._mp) return;
    this._mp.identify(String(id));
    if (traits && Object.keys(traits).length) this._mp.people.set(traits as Dict);
  },

  track(event: string, props?: Props): void {
    if (this._mp) this._mp.track(event, props as Dict);
  },

  pageview(path?: string, title?: string): void {
    if (!this._mp) return;
    // Suppress the generic Page Viewed event for routes that already fire a
    // dedicated, richer event (Profile View / Blog View / News View). The
    // super-property side effects (current_url etc.) still run in the facade
    // so other events keep their context — only the duplicate page-view
    // track call is skipped here.
    if (_pageViewSuppressed(path)) return;
    const cleanPath = _normalizePathForEventName(path);
    this._mp.track('Page Viewed ' + cleanPath, { path: path || '/', title });
  },

  reset(): void {
    if (this._mp) this._mp.reset();
  },

  trackCharge(amount: number, props?: Props): void {
    if (!this._mp) return;
    this._mp.people.track_charge(amount, props as Dict);
  },

  registerSuper(props: Props): void {
    if (this._mp) this._mp.register(props as Dict);
  },
} satisfies VendorAdapter & { _mp: Mixpanel | null };

const VENDORS: VendorAdapter[] = [
  MixpanelAdapter,
  // SegmentAdapter,
  // AmplitudeAdapter,
  // PostHogAdapter,
];

// ============================================================================
// Dispatch + queue
// ============================================================================

function _onAdapterReady(/* vendorName */): void {
  // Could track per-vendor readiness if needed. For now, drain when any adapter
  // signals ready — subsequent vendors will see live calls.
  _drainQueue();
}

function _drainQueue(): void {
  while (_queue.length) {
    const entry = _queue.shift();
    if (entry) _dispatch(entry[0], entry[1]);
  }
}

function _dispatch(method: string, args: unknown[]): void {
  VENDORS.forEach((v) => {
    try {
      const fn = (v as unknown as Record<string, unknown>)[method];
      if (typeof fn === 'function') (fn as (...a: unknown[]) => unknown).apply(v, args);
    } catch (e) {
      if (!IS_PROD) console.warn('[analytics:' + v.name + ':' + method + ']', e);
    }
  });
}

function _enqueueOrDispatch(method: string, args: unknown[]): void {
  if (_state.ready) _dispatch(method, args);
  else _queue.push([method, args]);
}

function _mergeSuper(props?: Props): Props {
  return { ..._superProps, ...(props || {}) };
}

// ============================================================================
// Public API
// ============================================================================

/** Call once on app boot (from Layout.tsx or main.tsx). Idempotent. */
export async function init(): Promise<void> {
  if (_state.ready) return;
  _captureAttribution();
  _superProps.app = APP_NAME;
  _superProps.app_version = APP_VERSION;

  await Promise.all(
    VENDORS.map((v) =>
      Promise.resolve()
        .then(() => v.init && v.init())
        .catch((e) => {
          if (!IS_PROD) console.warn('[analytics:' + v.name + ':init]', e);
        })
    )
  );
  _state.ready = true;

  // Re-apply identity captured before adapters finished loading.
  if (_pendingIdentity) {
    const u = _pendingIdentity;
    _pendingIdentity = null;
    identify(u);
  }
  _drainQueue();
}

/** Pass the full user object from Redux. Extracts canonical traits. */
export function identify(user: AnalyticsUser | null | undefined): void {
  if (!user || !user.ID) return;
  if (!_state.ready) {
    _pendingIdentity = user;
    return;
  }

  const traits = _userTraits(user);
  // Super properties auto-attach to every event. Keep only the truly
  // event-agnostic facets here — `gender` and `country` were noisy on
  // post-related events and are still available as people-profile traits
  // for cohort/breakdown queries in Mixpanel.
  registerSuper({
    plan: traits.plan,
    is_logged: true,
  });
  _enqueueOrDispatch('identify', [user.ID, traits]);
}

/** Fire a tracked event. Prefer EVENTS.* constants over raw strings. */
export function track(event: string, props?: Props): void {
  if (!event) return;
  _enqueueOrDispatch('track', [event, _mergeSuper(props)]);
}

/**
 * Manual pageview — also updates route_layout, current/previous URL super
 * properties so every subsequent event automatically carries the page where it
 * fired and the page the user came from (in-app SPA referrer).
 */
export function pageview(path?: string, title?: string): void {
  const currentUrl =
    typeof window !== 'undefined' && window.location ? window.location.href : path;
  registerSuper({
    route_layout: _layoutFromPath(path),
    previous_url: _superProps.current_url || null,
    previous_path: _superProps.current_path || null,
    current_url: currentUrl,
    current_path: path || null,
    current_title: title || null,
  });
  _enqueueOrDispatch('pageview', [path, title]);
}

/** Call on logout — clears identity across all vendors. */
export function reset(): void {
  _pendingIdentity = null;
  delete _superProps.plan;
  delete _superProps.is_logged;
  // Defensive cleanup in case an older session set these (we no longer set
  // them on identify, but be safe for users with cached super props).
  delete _superProps.gender;
  delete _superProps.country;
  _enqueueOrDispatch('reset', []);
}

/** Revenue helper — call on successful purchase. amount in major currency unit. */
export function trackCharge(amount: number | string, props?: Props): void {
  const n = Number(amount);
  if (!isFinite(n) || n <= 0) return;
  _enqueueOrDispatch('trackCharge', [n, _mergeSuper(props)]);
  // Also emit a normal event so it shows up in funnels.
  track(EVENTS.SUBSCRIPTION_PURCHASED, { amount: n, ...(props || {}) });
}

/** Register a super property — sent with every subsequent event. */
export function registerSuper(props: Props): void {
  if (!props || typeof props !== 'object') return;
  Object.assign(_superProps, props);
  _enqueueOrDispatch('registerSuper', [props]);
}

/**
 * Track an outbound / external-link click. Use this at every `<a target="_blank">`
 * or `window.open(url)` that points off-domain so we can build attribution
 * funnels in Mixpanel. The `source` discriminator should describe WHERE the
 * link lived (e.g. `'news_read_full'`, `'profile_social'`, `'blog_body'`).
 * Best-effort host extraction — invalid / relative URLs are still tracked
 * with `host: null` so the click isn't lost.
 */
export function trackExternalLink(
  url: string,
  source: string,
  extras?: Props,
): void {
  if (!url) return;
  let host: string | null = null;
  try {
    host = new URL(url, typeof window !== 'undefined' ? window.location.href : 'http://localhost').hostname;
  } catch {
    /* malformed URL — host stays null */
  }
  track(EVENTS.EXTERNAL_LINK_CLICKED, {
    url,
    host,
    source,
    ...(extras || {}),
  });
}

/**
 * Normalize an Api response into { success, error_message, error_code } props.
 * Use to track both success AND failure of any action in one event.
 *   analytics.track('Post Created', { type, ...analytics.withResult(res) });
 */
export function withResult(res?: ApiResult | null): ResultProps {
  const success = !!(res && res.success);
  const out: ResultProps = { success };
  if (!success) {
    out.error_message =
      (res && (res.message || (res.data && res.data.message))) || 'unknown_error';
    if (res && res.code) out.error_code = res.code;
  }
  return out;
}

// ============================================================================
// Helpers (kept private so call sites stay simple)
// ============================================================================

function _userTraits(user: AnalyticsUser): UserTraits {
  const fullname = [user.firstname, user.lastname].filter(Boolean).join(' ');
  const profile = user.profile || {};
  const extra = user.extra || {};
  const age = parseInt(String(profile.age ?? extra.age ?? ''), 10) || undefined;
  return {
    $name: fullname || user.username,
    // Privacy: send a synthesized email as the people-trait identity so the real
    // email is never persisted on the Mixpanel user profile. Raw email is still
    // sent on login/register *events* (event properties), per product decision.
    $email: user.username ? user.username + '@yaarikut.com' : undefined,
    $first_name: user.firstname || undefined,
    $last_name: user.lastname || undefined,
    // Mixpanel recognizes $avatar as the profile picture URL.
    $avatar: user.image || user.thumbnail || undefined,
    $created: user.datetime_created || undefined,
    username: user.username,
    plan: user.plan || 'free',
    gender: user.gender || undefined,
    birthdate: user.birthdate || undefined,
    age,
    age_bucket: _ageBucket(age),
    city: user.city || profile.city || undefined,
    state: user.state || profile.state || undefined,
    country: user.country || profile.country || undefined,
    location:
      extra.location ||
      profile.location_text ||
      (typeof profile.location === 'string' ? profile.location : undefined) ||
      undefined,
    photo: user.image || user.thumbnail || undefined,
    profile_completion: user.complete_profile && user.complete_profile.per,
    is_email_verified: !!user.emailverify,
    has_subscription: (() => {
      // Defensive `.plan` access — `subscription` is typed `unknown` so
      // every shape lands cleanly; we extract a string `plan` if one is
      // present and non-"free".
      const sub = user.subscription as { plan?: unknown } | null | undefined;
      const plan = sub && typeof sub.plan === 'string' ? sub.plan : '';
      return !!plan && plan !== 'free';
    })(),
  };
}

function _ageBucket(age?: number | string): string | undefined {
  const n = parseInt(String(age ?? ''), 10);
  if (!n) return undefined;
  if (n < 18) return '<18';
  if (n < 25) return '18-24';
  if (n < 35) return '25-34';
  if (n < 45) return '35-44';
  if (n < 55) return '45-54';
  if (n < 65) return '55-64';
  return '65+';
}

/**
 * Returns true if a route fires its own dedicated view event (Profile View /
 * Blog View / News View) and the generic Page Viewed event should NOT fire.
 */
function _pageViewSuppressed(path?: string): boolean {
  if (!path) return false;
  const p = path.split('?')[0].split('#')[0].replace(/\/$/, '');
  if (/^\/user\/[^/]+/i.test(p)) return true; // all /user/... profile pages
  if (/^\/blogs\/[^/]+\/[^/]+/i.test(p)) return true; // blog detail
  if (/^\/news\/[^/]+\/[^/]+/i.test(p)) return true; // news article
  if (/^\/tv(\/|$)/i.test(p)) return true; // /tv/* (own LiveTV events)
  return false;
}

/**
 * Strip query string, hash, and trailing slash. Preserves the literal path
 * (including usernames, IDs, etc.) so each unique URL gets its own event name
 * in Mixpanel — e.g. `Page Viewed /user/jeetgill` rather than a normalized
 * placeholder. Higher cardinality, but the URL is directly visible.
 */
function _normalizePathForEventName(path?: string): string {
  if (!path) return '/';
  let p = path.split('?')[0].split('#')[0];
  if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);
  return p || '/';
}

function _layoutFromPath(path?: string): string {
  if (!path) return 'unknown';
  if (path.startsWith('/dashboard') || path.startsWith('/profile/')) return 'dashboard';
  if (path.startsWith('/user/')) return 'profile';
  return 'default';
}

/**
 * Default export mirrors the legacy module's default export shape so existing
 * `import analytics from '...'` call sites keep working.
 */
const analytics = {
  init,
  identify,
  track,
  pageview,
  reset,
  trackCharge,
  registerSuper,
  trackExternalLink,
  withResult,
  EVENTS,
};

export { analytics };
export default analytics;
